/**
 * Raising and clearing OS-level notification banners.
 *
 * Everything goes through the service worker rather than `new Notification()`, for
 * two reasons. Safari refuses the constructor outright in some contexts and wants
 * the service-worker path. And more importantly, a banner raised by the page cannot
 * be found again: `getNotifications()` enumerates what a *registration* raised, so a
 * page-created banner can only be closed through a reference held since it was
 * shown — which doesn't survive a reload, let alone another tab.
 *
 * Being able to find them again is the whole point, because a banner the user has
 * already dealt with has to go away by itself. Off-screen is not cleared: the banner
 * slides away on its own after a few seconds, but it stays in Notification Center on
 * macOS and the Action Center on Windows until something closes it. Since the server
 * raises its own banners for the same conversation through this same worker, and
 * both sides tag identically (`buildChannelNotificationTag`), clearing by tag clears
 * both at once.
 */

/**
 * The installed app's icon (manifest.json), also used by the service worker's push banners.
 * The small attribution icon beside it is drawn by the OS and may still identify Chrome.
 */
export const NOTIFICATION_ICON = '/web-app-manifest-192x192.png'

/**
 * `navigator.serviceWorker.ready` never settles when no worker ever activates, so
 * awaiting it alone would silently swallow every banner on a failed registration.
 */
const REGISTRATION_TIMEOUT_MS = 2_000

export interface DesktopNotification {
  title: string
  body: string
  /** Notifications sharing a tag replace one another instead of stacking. */
  tag: string
  /** In-app path the banner opens when clicked. */
  link: string
  /**
   * Suppress the OS's own notification sound. Set when the caller has already made
   * the noise itself, so that one message does not produce two.
   */
  silent?: boolean
}

/**
 * Banners raised through the fallback below, kept so they can still be cleared by
 * tag. Bounded by the number of distinct conversations, and an entry is replaced
 * rather than added when the same tag fires again.
 */
const pageNotifications = new Map<string, Notification>()

const getRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null

  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), REGISTRATION_TIMEOUT_MS)),
    ])
  } catch {
    return null
  }
}

/**
 * Last resort where there is no service worker to raise it.
 *
 * Routes on click like the worker's `notificationclick` does, rather than only
 * surfacing a window the reader was already looking past. Safe to navigate
 * `window`: the handler is a closure held by the page that raised the banner, so
 * it can only run while that page is alive. A full navigation rather than the app
 * router because this module is pulled in by store construction, and importing
 * the router here would close a cycle back through the pages it builds.
 */
const showThroughPage = ({ title, body, tag, silent, link }: DesktopNotification): void => {
  try {
    const notification = new Notification(title, { body, tag, silent, icon: NOTIFICATION_ICON })
    notification.onclick = () => {
      window.focus()
      window.location.assign(link)
      notification.close()
    }
    pageNotifications.set(tag, notification)
  } catch {
    // Safari throws on the constructor in some contexts. The sound and the badge
    // still land, so swallow it.
  }
}

export const showDesktopNotification = async (notification: DesktopNotification): Promise<void> => {
  const registration = await getRegistration()

  if (registration) {
    try {
      await registration.showNotification(notification.title, {
        body: notification.body,
        tag: notification.tag,
        silent: notification.silent,
        icon: NOTIFICATION_ICON,
        // Read by the worker's `notificationclick` handler, which focuses an open
        // Atlas window and navigates it rather than opening a second one.
        data: { link: notification.link },
      })
      return
    } catch {
      // Fall through — a worker that can't show it is no better than not having one.
    }
  }

  showThroughPage(notification)
}

/**
 * Close any banner raised under these tags, on the way out of a conversation the
 * user has now read. Silent when there is nothing open under them.
 */
export const clearDesktopNotifications = async (tags: string[]): Promise<void> => {
  tags.forEach((tag) => {
    const notification = pageNotifications.get(tag)
    if (!notification) return
    notification.close()
    pageNotifications.delete(tag)
  })

  const registration = await getRegistration()
  if (!registration) return

  try {
    await Promise.all(
      tags.map(async (tag) => {
        const open = await registration.getNotifications({ tag })
        open.forEach((notification) => notification.close())
      }),
    )
  } catch {
    // Nothing to do if the browser won't enumerate them — the banner expires from
    // the notification centre on its own eventually, and clicking it still clears it.
  }
}
